import React from 'react';
import { motion } from 'framer-motion';
import './AboutUs.css';
import { FaUsers, FaAward, FaGlobe, FaIndustry } from 'react-icons/fa';
import image from '../../Assets/image.png';
import { useSiteContent } from '../../context/SiteContentContext';

const DEFAULT_STATS = [
  { icon: <FaUsers />, number: "500+", label: "Happy Clients" },
  { icon: <FaAward />, number: "29", label: "Years Experience" },
  { icon: <FaGlobe />, number: "50+", label: "Countries Served" },
  { icon: <FaIndustry />, number: "1000+", label: "Products Delivered" }
];

const DEFAULT_WHY = [
  { title: 'Expertise & Experience', text: '29+ years of industry experience with deep technical knowledge and proven track record.' },
  { title: 'Quality Assurance', text: 'ISO certified processes ensuring consistent quality and reliability in every product.' },
  { title: 'Global Reach', text: 'Serving clients across 50+ countries with localized support and service.' },
];

const AboutUs = () => {
  const { siteContent, isSiteContentLoading } = useSiteContent();

  const content = {
    about_hero_title: siteContent?.about_hero_title || 'About HKI',
    about_hero_subtitle:
      siteContent?.about_hero_subtitle || 'Leading the Future of Precision Engineering Since 1995',
    about_overview_heading: siteContent?.about_overview_heading || 'Who We Are',
    about_overview_text: siteContent?.about_overview_text || '',
    about_why_title: siteContent?.about_why_title || 'Why Choose HKI?',
    about_stats: Array.isArray(siteContent?.about_stats) ? siteContent.about_stats : [],
    about_why_items: Array.isArray(siteContent?.about_why_items) ? siteContent.about_why_items : [],
    about_image_url: siteContent?.about_image_url || null,
  };

  const companyStats = content.about_stats.length > 0
    ? [0, 1, 2, 3].map((i) => {
        const s = content.about_stats[i] || {};
        const def = DEFAULT_STATS[i] || {};
        return {
          icon: def.icon,
          number: s.number ?? def.number ?? '',
          label: s.label ?? def.label ?? '',
        };
      })
    : DEFAULT_STATS;

  const whyItems = content.about_why_items.length >= 3
    ? content.about_why_items
    : DEFAULT_WHY;

  const overviewParagraphs = content.about_overview_text
    ? content.about_overview_text.split(/\n\n+/).filter((p) => p.trim())
    : [
        'HKI (Hari Krishna Industries) stands as a beacon of excellence in the precision engineering industry. For over 29 years, we have been at the forefront of manufacturing high-quality industrial valves, fittings, and precision components that power industries worldwide.',
        "Our journey began with a simple yet powerful vision: to make precision engineering accessible and reliable for all businesses, regardless of their scale or sector. Today, we are proud to serve clients across 50+ countries, delivering solutions that meet the most demanding industrial requirements.",
      ];

  if (isSiteContentLoading && !siteContent) {
    return (
      <div className="about-us-page">
        <div className="about-us-loading" style={{ padding: '80px 20px', textAlign: 'center', color: '#666' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="about-us-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-container">
          <motion.div 
            className="about-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="about-hero-title">{content.about_hero_title}</h1>
            <p className="about-hero-subtitle">
              {content.about_hero_subtitle}
            </p>
            <div className="about-hero-accent"></div>
          </motion.div>
        </div>
      </section>

      {/* Company Overview Section */}
      <section className="company-overview">
        <div className="overview-container">
          <div className="overview-content">
            <motion.div 
              className="overview-text"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2>{content.about_overview_heading}</h2>
              {overviewParagraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </motion.div>
            <motion.div 
              className="overview-image"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="image-container">
                <img
                  src={content.about_image_url || image}
                  alt="Hari Krishna Industries precision engineering and manufacturing overview"
                  loading="lazy"
                  decoding="async"
                />
                <div className="image-overlay">
                  <div className="overlay-content">
                    <span className="overlay-text">Precision</span>
                    <span className="overlay-text">Quality</span>
                    <span className="overlay-text">Innovation</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="stats-section">
        <div className="stats-container">
          {companyStats.map((stat, index) => (
            <motion.div 
              key={index}
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="why-choose-container">
          <motion.h2 
            className="why-choose-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {content.about_why_title}
          </motion.h2>
          <div className="why-choose-grid">
            {whyItems.map((item, index) => (
              <motion.div 
                key={index}
                className="why-choose-item"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: (index + 1) * 0.1 }}
              >
                <h3>{item.title || `Item ${index + 1}`}</h3>
                <p>{item.text || ''}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
